// Mi espacio › Objetivos y revisión · revisión semanal (pure, tested by scripts/test-weekly-review.mjs).
// What happened in one week (Monday to Sunday, in the time zone chosen in Hoy) and, apart, what needs attention today.
// Everything here only arranges records already loaded (Mi espacio's own, the commercial data of the screen and the
// owner's read-only money view): nothing is read, written or stored. Nothing is reconstructed: a date that is missing
// is said, never guessed, and a source that did not load is a failure, never a zero.
import { outreachKind } from './outreachEvidence.js';
import { chainsOf } from './quoteChains.js';
import { isAccepted } from './quoteRules.js';
import { addDaysIso } from './coachingTime.js';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const linked = (r, k) => (Array.isArray(r?.fields?.[k]) ? r.fields[k].map(x => (typeof x === 'string' ? x : x?.id)).filter(Boolean) : []);
const F = (r, k) => { const v = r?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? '') : (v ?? ''); };

/** Monday of the week of a local day (YYYY-MM-DD). */
export function mondayOf(day) {
  if (!DAY_RE.test(day || '')) return null;
  const d = new Date(`${day}T12:00:00Z`);
  return addDaysIso(day, -((d.getUTCDay() + 6) % 7));
}
export const weekOf = (weekStart) => ({ start: weekStart, end: addDaysIso(weekStart, 6) });

/**
 * The local day of a stored date in Ale's time zone. A date without time (YYYY-MM-DD) is that day as written (never
 * shifted by a time zone); a date with time is converted to her day. Anything else: null (no day, never a guess).
 */
export function localDayOfValue(v, tz) {
  const s = String(v ?? '').trim();
  if (DAY_RE.test(s)) return s;
  const t = Date.parse(s);
  if (!s || !Number.isFinite(t)) return null;
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(t)); }
  catch { return new Date(t).toISOString().slice(0, 10); }
}
const inWeek = (day, w) => !!day && day >= w.start && day <= w.end;

const CONTACT_KINDS = ['gmail_sent', 'confirmed', 'logged'];
/**
 * The commercial activity of the week (Outreach): interactions by kind and, apart, how many people. A contact made is
 * a verified email (gmail_sent), a contact Ale confirmed (confirmed) or one registered by hand (logged, not verified).
 * An answer is theirs (inbound). Opening a channel (opened) never counts as a contact; drafts never count.
 */
export function commercialWeek({ outreach = [], stakeholders = [], tz, weekStart }) {
  const w = weekOf(weekStart);
  const name = new Map(stakeholders.map(s => [s.id, `${F(s, 'Name')} ${F(s, 'Last name')}`.trim() || 'Contacto']));
  const kinds = { gmail_sent: [], confirmed: [], logged: [], inbound: [], opened: [] };
  let withoutDay = 0;
  for (const o of outreach) {
    const kind = outreachKind(o);
    if (!kinds[kind]) continue; // drafts and pending items are not interactions
    const day = localDayOfValue(o.fields?.['Date'], tz);
    if (!day) { withoutDay++; continue; }
    if (!inWeek(day, w)) continue;
    const sid = linked(o, 'Stakeholder')[0] || null;
    kinds[kind].push({ id: o.id, day, stakeholderId: sid, name: sid ? name.get(sid) || 'Contacto' : 'Sin contacto vinculado', channel: F(o, 'Channel'), by: F(o, 'Logged By') });
  }
  for (const k of Object.keys(kinds)) kinds[k].sort((a, b) => a.day.localeCompare(b.day));
  const people = (list) => new Set(list.map(x => x.stakeholderId).filter(Boolean)).size;
  const contacts = CONTACT_KINDS.flatMap(k => kinds[k]);
  return {
    kinds,
    contacts: { interactions: contacts.length, people: people(contacts) },
    answers: { interactions: kinds.inbound.length, people: people(kinds.inbound) },
    opened: { interactions: kinds.opened.length },
    withoutDay,
  };
}

/**
 * The quotes accepted in the week: one per chain of versions (its latest accepted version), by its acceptance date
 * (Won At) in Ale's day. An accepted version without a date is listed apart as «Aceptada, sin fecha»: it is never put
 * in a week.
 */
export function acceptedWeek({ intakes = [], tz, weekStart }) {
  const w = weekOf(weekStart);
  const inside = [], undated = [];
  for (const chain of chainsOf(intakes)) {
    const sale = chain.sale;
    if (!sale || !isAccepted(sale.record.fields || {})) continue;
    const f = sale.record.fields || {};
    const day = localDayOfValue(f['Won At'], tz);
    const item = { id: sale.id, name: String(f['Name'] || 'Cotización'), version: sale.number, currency: String(F(sale.record, 'Quote Currency') || ''), day };
    if (!day) undated.push(item);
    else if (inWeek(day, w)) inside.push(item);
  }
  return { inside: inside.sort((a, b) => a.day.localeCompare(b.day)), undated };
}

/**
 * Time of the week from Mi tiempo (ownerTime.buildWeek): the confirmed total includes the time still to classify
 * (shown as a part of it); time pending confirmation stays out of the total. Per goal, a stretch linked to several
 * goals counts in each of them: those figures are never added up.
 */
export function timeOfWeek(tw) {
  if (!tw) return null;
  return {
    confirmed: tw.real?.minutes || 0, unclassified: tw.unclassified?.minutes || 0, pending: tw.pending?.minutes || 0, pendingEntries: tw.pending?.entries?.length || 0,
    byCategory: tw.real?.byCategory || [], byGoal: (tw.byGoal || []).map(g => ({ id: g.goalId, title: g.title, minutes: g.effortMinutes, actions: g.actions.length, observed: g.observed.length })),
  };
}

/** Tasks completed in the week (Done On, a day) and goal progress written in the week. */
export function doneOfWeek({ tasks = [], goals = [], weekStart }) {
  const w = weekOf(weekStart);
  return {
    tasks: tasks.filter(t => t.status === 'done' && inWeek(t.doneOn, w)).sort((a, b) => String(a.doneOn).localeCompare(String(b.doneOn))),
    goals: goals.filter(g => g.status === 'active' || (g.progress || []).some(p => inWeek(p.date, w))).map(g => ({
      id: g.id, title: g.title, status: g.status,
      progress: (g.progress || []).filter(p => inWeek(p.date, w)),
      tasksDone: tasks.filter(t => t.status === 'done' && inWeek(t.doneOn, w) && (t.goalIds || []).includes(g.id)).length,
    })),
  };
}

/** Money of the period (payments received, refunds, net) per currency, never added across currencies. */
export function moneyOfWeek(summary) {
  if (!summary) return null;
  return Object.entries(summary.byCurrency || {}).map(([currency, b]) => ({
    currency, received: b.received.amount, receivedCount: b.received.items.length, refunds: b.refunds.amount, refundCount: b.refunds.items.length, net: b.net,
    items: [...b.received.items.map(i => ({ ...i, kind: 'received' })), ...b.refunds.items.map(i => ({ ...i, kind: 'refund' }))].sort((a, b2) => String(a.day).localeCompare(String(b2.day))),
  })).filter(x => x.receivedCount || x.refundCount);
}

/**
 * What needs attention today (never reconstructed for a past week): overdue next actions of contacts, other overdue
 * tasks and goal actions, answers waiting, overdue instalments per currency (balance today) and active goals without a
 * next step. Each item names the record it opens.
 */
export function attentionToday({ day = null, replies = [], money = null, focus = [] }) {
  const overdue = (day?.overdue || []);
  const contactTasks = overdue.filter(t => t.source?.kind === 'contact' && t.source.stakeholderId);
  const otherTasks = overdue.filter(t => !(t.source?.kind === 'contact' && t.source.stakeholderId));
  return {
    contactTasks: contactTasks.map(t => ({ id: t.id, title: t.title, dueOn: t.dueOn, stakeholderId: t.source.stakeholderId })),
    tasks: otherTasks.map(t => ({ id: t.id, title: t.title || t.idea, dueOn: t.dueOn || t.plannedOn, type: t.type || 'task' })),
    replies: replies.map(r => ({ id: r.id, name: r.name, daysSince: r.daysSince })),
    overdueMoney: money ? Object.entries(money.byCurrency || {}).filter(([, b]) => b.overdue.items.length).map(([currency, b]) => ({ currency, amount: b.overdue.amount, items: b.overdue.items })) : null,
    goals: focus.filter(g => g.status === 'active' && !g.next).map(g => ({ id: g.id, title: g.title })),
  };
}

/** Minutes → «7 h 40 min». */
export const hm = (m) => { const n = Math.max(0, Math.round(m || 0)); return `${Math.floor(n / 60)} h ${String(n % 60).padStart(2, '0')} min`; };
